import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Stack,
  CircularProgress,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import {
  fetchBookingSetting,
  getBookingSetting,
  resetBookingSetting,
  upsertPricingConfig,
} from "../../../dux/bookingSetting";
import { isApiPending } from "../../../dux/apiStatus";

const turfColors = {
  // For new format with numeric turf keys
  1: "#e3f2fd",
  2: "#fce4ec",
  3: "#fff3e0",
  4: "#e8f5e9",
};

// Location and sport-specific turf configuration mapping
const locationSportTurfConfig = {
  srinagar: {
    "Cricket Net": ["1", "2", "3", "4"],
    "Pickle Ball": ["1", "2"],
    Football: ["1", "2", "3"],
    "Box Cricket": ["1", "2", "3"],
  },
  // Add more locations as needed
  // baramulla: {
  //   // "Cricket Net": ["1", "2"],
  //   "Pickle Ball": ["1", "2"],
  //   // Football: ["1", "2"],
  //   // "Box Cricket": ["1", "2"],
  // },
  jammu: {
    "Pickle Ball": ["1", "2", "3", "4"],
  },
};

// Location and sport-specific turf name mapping
const locationSportTurfNames = {
  srinagar: {
    "Cricket Net": {
      1: "Net A (3 People Max)",
      2: "Net B (3 People Max)",
      3: "Net A (6 People Max)",
      4: "Net B (6 People Max)",
    },
    "Pickle Ball": {
      1: "Court A",
      2: "Court B",
    },
    Football: {
      1: "5 a side Turf A",
      2: "5 a side Turf B",
      3: "7 a side Turf C",
    },
    "Box Cricket": {
      1: "Turf A",
      2: "Turf B",
      3: "Turf C",
    },
  },
  // Add more locations as needed
  // baramulla: {
  //   // "Cricket Net": {
  //   //   1: "Net 1",
  //   //   2: "Net 2",
  //   // },
  //   "Pickle Ball": {
  //     1: "Court A",
  //     2: "Court B",
  //   },
  //   // Football: {
  //   //   1: "Ground A",
  //   //   2: "Ground B",
  //   // },
  //   // "Box Cricket": {
  //   //   1: "Box 1",
  //   //   2: "Box 2",
  //   // },
  // },
  jammu: {
    "Pickle Ball": {
      1: "Court 1",
      2: "Court 2",
      3: "Court 3",
      4: "Court 4",
    },
  },
};

const formatTime = (value) => {
  if (!value || !dayjs(value).isValid()) return "00:00";
  const hours = dayjs(value).hour().toString().padStart(2, "0");
  const minutes = dayjs(value).minute().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

const isTimeOverlap = (slots) => {
  const sorted = [...slots].sort((a, b) =>
    a.startTime.localeCompare(b.startTime)
  );
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].endTime > sorted[i + 1].startTime) return true;
  }
  return false;
};

const GeneralSetting = ({ sport, location }) => {
  const dispatch = useDispatch();
  const [isSaveSettingDialogOpen, setSaveSettingDialogOpen] = useState(false);
  const booking_setting = useSelector(getBookingSetting);
  const [localConfig, setLocalConfig] = useState(null);
  const [hasAnyOverlap, setHasAnyOverlap] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const isLoading = useSelector((state) =>
    isApiPending(state, "bookingSetting")
  );
  const isSaving = useSelector((state) =>
    isApiPending(state, "upsertPricingConfig")
  );

  // Map sport names to API format
  const sportMap = useMemo(
    () => ({
      "Cricket Net": "cricket-net",
      "Pickle Ball": "pickle-ball",
      Football: "football",
      "Box Cricket": "box-cricket",
    }),
    []
  );

  useEffect(() => {
    setIsInitialLoading(true);
    dispatch(resetBookingSetting());
    dispatch(fetchBookingSetting({ sport: sportMap[sport], location }));
  }, [sport, location, dispatch, sportMap]);

  useEffect(() => {
    setLocalConfig(booking_setting?.bookingSetting);
    if (booking_setting?.bookingSetting) {
      setIsInitialLoading(false);
    }
  }, [booking_setting]);

  useEffect(() => {
    if (!localConfig) return;

    // The new API response has NORMAL and HOLIDAY directly at the root level
    const settings = localConfig;
    let anyOverlap = false;

    // Check for overlaps in both NORMAL and HOLIDAY sections
    ["NORMAL", "HOLIDAY"].forEach((key) => {
      if (settings?.[key]) {
        // Iterate through turf numbers (1, 2, 3, 4)
        Object.keys(settings[key]).forEach((turfKey) => {
          const slots = [...settings[key][turfKey]]; // Create a copy to avoid mutation
          if (Array.isArray(slots)) {
            // Sort based on startTime
            slots.sort((a, b) => a.startTime.localeCompare(b.startTime));
            // Check overlap
            if (isTimeOverlap(slots)) {
              anyOverlap = true;
            }
          }
        });
      }
    });

    setHasAnyOverlap(anyOverlap);
  }, [localConfig, sport, location]);

  const handleSave = () => {
    if (hasAnyOverlap) return;

    // Format the payload for the new upsert API
    const payload = {
      sport: sportMap[sport] || sport.toLowerCase().replace(/\s+/g, "-"),
      location: location.toLowerCase(),
      NORMAL: localConfig?.NORMAL || {},
      HOLIDAY: localConfig?.HOLIDAY || {},
    };

    dispatch(
      upsertPricingConfig({ json: payload, sport: sportMap[sport], location })
    );
    setSaveSettingDialogOpen(false);
  };

  const handleReset = () => {
    setIsInitialLoading(true);
    setLocalConfig(booking_setting?.bookingSetting);
    setHasAnyOverlap(false);
    dispatch(resetBookingSetting());
    dispatch(fetchBookingSetting({ sport: sportMap[sport], location }));
  };

  const handleDeleteRow = (dataKey, turfKey, index) => {
    if (!localConfig?.[dataKey]?.[turfKey]) return;

    const updated = { ...localConfig };
    updated[dataKey] = { ...updated[dataKey] };
    updated[dataKey][turfKey] = [...updated[dataKey][turfKey]];
    updated[dataKey][turfKey].splice(index, 1);
    setLocalConfig(updated);
  };

  const handleAddRow = (dataKey, turfKey) => {
    const updated = { ...localConfig };

    // Ensure dataKey exists
    if (!updated[dataKey]) {
      updated[dataKey] = {};
    } else {
      updated[dataKey] = { ...updated[dataKey] };
    }

    // Ensure turfKey exists
    if (!updated[dataKey][turfKey]) {
      updated[dataKey][turfKey] = [];
    } else {
      updated[dataKey][turfKey] = [...updated[dataKey][turfKey]];
    }

    // Add new row
    updated[dataKey][turfKey].push({
      startTime: "00:00",
      endTime: "00:00",
      cost: 0,
    });

    setLocalConfig(updated);
  };

  const handleFieldChange = (dataKey, turfKey, index, field, value) => {
    if (!localConfig?.[dataKey]?.[turfKey]?.[index]) return;

    const updated = { ...localConfig };
    updated[dataKey] = { ...updated[dataKey] };
    updated[dataKey][turfKey] = [...updated[dataKey][turfKey]];
    updated[dataKey][turfKey][index] = {
      ...updated[dataKey][turfKey][index],
      [field]: value,
    };
    setLocalConfig(updated);
  };

  const renderRoutineColumn = (label, dataKey) => {
    // The new API response has NORMAL and HOLIDAY directly at the root level
    const routineData = localConfig?.[dataKey] || {};

    // Get location and sport-specific turf keys
    const allTurfKeys = locationSportTurfConfig[location]?.[sport] || [
      "1",
      "2",
      "3",
      "4",
    ]; // Fallback to all turfs

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          flex: 1,
        }}
      >
        {allTurfKeys.map((turfKey) => {
          const turfData = routineData?.[turfKey] || [];

          // Only check for overlap if there are time slots
          const hasOverlap = turfData.length > 0 && isTimeOverlap(turfData);
          if (hasOverlap && !hasAnyOverlap) setHasAnyOverlap(true);

          // Get location and sport-specific turf display name
          const turfDisplayName =
            locationSportTurfNames[location]?.[sport]?.[turfKey] ||
            `Turf ${turfKey}`;

          return (
            <div
              key={`${label}-${turfKey}`}
              style={{
                background: turfColors[turfKey] || "#f0f0f0",
                borderRadius: "10px",
                padding: "20px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              }}
            >
              <div
                style={{
                  background: "#09213c",
                  color: "white",
                  padding: "10px",
                  borderRadius: "6px",
                  fontWeight: "bold",
                }}
              >
                {label} - {turfDisplayName}
              </div>
              {hasOverlap && (
                <p style={{ color: "#D32F2F", fontWeight: "bold" }}>
                  ⚠️ Overlapping time slots detected!
                </p>
              )}
              {turfData.length === 0 && (
                <p
                  style={{
                    color: "#666",
                    fontStyle: "italic",
                    margin: "10px 0",
                  }}
                >
                  No time slots configured. Click "Add Time Slot" to get
                  started.
                </p>
              )}
              {turfData.map((row, idx) => {
                const isConflict = turfData.some(
                  (other, i) =>
                    i !== idx &&
                    row.startTime < other.endTime &&
                    row.endTime > other.startTime
                );
                return (
                  <div
                    key={`${dataKey}-${turfKey}-${idx}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      margin: "10px 0",
                      background: isConflict ? "#ffebee" : "white",
                      padding: "10px",
                      borderRadius: "6px",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <TimePicker
                      label="Start Time"
                      value={dayjs(`2023-01-01T${row.startTime}`)}
                      onChange={(newValue) =>
                        handleFieldChange(
                          dataKey,
                          turfKey,
                          idx,
                          "startTime",
                          formatTime(newValue)
                        )
                      }
                      ampm={false}
                      slotProps={{ textField: { size: "small" } }}
                    />
                    <TimePicker
                      label="End Time"
                      value={dayjs(`2023-01-01T${row.endTime}`)}
                      onChange={(newValue) =>
                        handleFieldChange(
                          dataKey,
                          turfKey,
                          idx,
                          "endTime",
                          formatTime(newValue)
                        )
                      }
                      ampm={false}
                      slotProps={{ textField: { size: "small" } }}
                    />
                    <TextField
                      label="Cost"
                      type="number"
                      value={row.cost}
                      onChange={(e) =>
                        handleFieldChange(
                          dataKey,
                          turfKey,
                          idx,
                          "cost",
                          parseFloat(e.target.value)
                        )
                      }
                      size="small"
                    />
                    <IconButton
                      onClick={() => handleDeleteRow(dataKey, turfKey, idx)}
                    >
                      <DeleteIcon style={{ color: "#D32F2F" }} />
                    </IconButton>
                  </div>
                );
              })}
              <Button
                variant="outlined"
                onClick={() => handleAddRow(dataKey, turfKey)}
                style={{
                  marginTop: "10px",
                  borderColor: "#09213c",
                  color: "#09213c",
                }}
                startIcon={<AddIcon />}
              >
                Add Time Slot
              </Button>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          alignItems="center"
          style={{ margin: "10px 20px" }}
        >
          <Tooltip title="Reset all changes to last saved version">
            <Button
              onClick={handleReset}
              variant="outlined"
              startIcon={
                isLoading ? <CircularProgress size={20} /> : <RestartAltIcon />
              }
              color="secondary"
              disabled={isLoading}
            >
              Reset
            </Button>
          </Tooltip>
          <Tooltip
            title={
              hasAnyOverlap
                ? "Fix overlapping time slots before saving."
                : "Save Settings"
            }
          >
            <span>
              <Button
                variant="contained"
                style={{ backgroundColor: "#FFD700", color: "black" }}
                onClick={() => setSaveSettingDialogOpen(true)}
                disabled={hasAnyOverlap || isSaving}
              >
                {isSaving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </span>
          </Tooltip>
        </Stack>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "30px",
            padding: "20px",
            background: "#f9f9f9",
          }}
        >
          {isLoading || isInitialLoading || !localConfig ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                p: 4,
                gap: 2,
              }}
            >
              <CircularProgress size={40} />
              <div style={{ color: "#666", fontSize: "16px" }}>
                {isLoading
                  ? "Loading pricing configuration..."
                  : "Initializing..."}
              </div>
            </Box>
          ) : (
            <>
              {renderRoutineColumn("Normal Day", "NORMAL")}
              {renderRoutineColumn("Holiday", "HOLIDAY")}
            </>
          )}
        </div>
        <Dialog open={isSaveSettingDialogOpen}>
          <DialogTitle>Confirm Save</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to save the setting ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setSaveSettingDialogOpen(false)}
              color="primary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              style={{ backgroundColor: "#FFD700", color: "black" }}
              autoFocus
              disabled={hasAnyOverlap || isSaving}
            >
              {isSaving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </div>
  );
};

export default GeneralSetting;
